import React, { Component } from "react";
import { connect } from "react-redux";
import { bool, func, shape } from "prop-types";
import { Col, Row } from "react-bootstrap";
import { Form, Field, Formik } from "formik";
import * as Yup from "yup";

import { push } from "connected-react-router";

import { selectors as investmentfundsSelectors, actions as investmentfundsActions } from "reducers/investmentfunds";

import * as i18n from "util/i18n";
import { emailRegex } from "util/form";
import * as configUtils from "util/config";

import PageLoading from "pages/_components/PageLoading";
import MainContainer from "pages/_components/MainContainer";
import Container from "pages/_components/Container";
import Button from "pages/_components/Button";
import TextField from "pages/_components/fields/TextField";
import Notification from "pages/_components/Notification";
import { ResponsiveHeader } from "pages/_components/header";
import PhoneInput from "pages/_components/fields/PhoneInput";
import FieldLabel from "pages/_components/fields/FieldLabel";
import I18n from "pages/_components/I18n";
import DocumentUploader from "../_components/DocumentUploader";

const FORM_ID = "investmentFunds.add.account";

class AddAccountForm extends Component {
    static propTypes = {
        dispatch: func.isRequired,
        fetchingSubmit: bool.isRequired,
        fetching: bool.isRequired,
        expired: bool.isRequired,
        accountData: shape({}).isRequired,
    };

    constructor(props) {
        super(props);
        this.formRef = React.createRef();
    }

    componentDidMount() {
        const { dispatch } = this.props;
        dispatch(investmentfundsActions.addAccountPre());
    }

    handleChangeName = (e, values) => {
        const { dispatch, accountData } = this.props;
        const buildAccountData = {
            ...values,
            documentToUpload: accountData?.documentToUpload,
            name: e.target.value,
        };
        dispatch(investmentfundsActions.setAccountData(buildAccountData));
    };

    handleChangeMail = (e, values) => {
        const { dispatch, accountData } = this.props;
        const buildAccountData = {
            ...values,
            documentToUpload: accountData?.documentToUpload,
            mail: e.target.value,
        };
        dispatch(investmentfundsActions.setAccountData(buildAccountData));
    };

    handleClickDownload = (filename) => {
        const link = document.createElement("a");
        link.href = `/templates/${filename}.xlsx`;
        link.download = `/templates/${filename}.xlsx`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    handleSubmit = (values) => {
        const { expired, accountData, dispatch } = this.props;
        const buildAccountData = {
            name: values.name,
            mail: values.mail,
            phone: `${values.phone.countryCode} ${values.phone.areaCode} ${values.phone.value}`,
            accountSummary: i18n.get(`${FORM_ID}.accountSummary.monthly`),
            documentToUpload: accountData.documentToUpload,
        };
        dispatch(investmentfundsActions.setAccountData(buildAccountData));
        if (expired) {
            dispatch(push("/investorTest"));
        } else {
            dispatch(push("/investmentFunds/accounts/add/ddjj"));
        }
    };

    validationSchema = () =>
        Yup.lazy(() =>
            Yup.object().shape({
                mail: Yup.string()
                    .required(i18n.get(`${FORM_ID}.mail.empty`))
                    .test(
                        "format",
                        i18n.get("onboarding.step.verifyEmail.email.error.invalidFormat"),
                        (value) => !!value && emailRegex.test(value),
                    ),
                phone: Yup.object().shape({
                    prefix: Yup.string(),
                    value: Yup.string().required(i18n.get(`${FORM_ID}.phone.empty`)),
                }),
            }),
        );

    render() {
        const { fetching, fetchingSubmit, accountData } = this.props;

        const initialValues = {
            name: accountData?.name || "",
            mail: accountData?.mail || "",
            phone: accountData?.phone
                ? {
                      countryCode: accountData.phone.countryCode
                          ? accountData.phone.countryCode
                          : accountData.phone.split(" ")[0],
                      areaCode: accountData.phone.areaCode
                          ? accountData.phone.areaCode
                          : accountData.phone.split(" ")[1],
                      value: accountData.phone.value
                          ? accountData.phone.value
                          : typeof accountData.phone === "string"
                          ? accountData.phone.split(" ")[2]
                          : "",
                  }
                : {
                      countryCode: "54",
                      areaCode: "11",
                      value: "",
                  },
        };

        return (
            <PageLoading loading={fetching}>
                <Notification scopeToShow="investmentfunds" />

                <ResponsiveHeader
                    id={`${FORM_ID}.title`}
                    className="header-without-button"
                    backLinkTo="/investmentFunds/accounts/noProfileInfo"
                />

                <MainContainer className="main-container">
                    <Formik
                        initialValues={initialValues}
                        onSubmit={this.handleSubmit}
                        validationSchema={this.validationSchema}
                        ref={this.formRef}>
                        {({ values }) => {
                            const canSubmit =
                                !values.mail ||
                                !values.phone ||
                                values.phone?.value === "" ||
                                !accountData?.documentToUpload;
                            return (
                                <Form className="above-the-fold">
                                    <Container className="items-center container--layout">
                                        <Row>
                                            <Col xs={12} md={6}>
                                                <Field
                                                    component={TextField}
                                                    idForm={FORM_ID}
                                                    name="name"
                                                    type="text"
                                                    hidePlaceholder
                                                    handleOnChange={(name) => this.handleChangeName(name, values)}
                                                />
                                            </Col>
                                        </Row>

                                        <Row>
                                            <Col xs={12} md={6} className="required">
                                                <Field
                                                    component={TextField}
                                                    idForm={FORM_ID}
                                                    name="mail"
                                                    type="text"
                                                    hidePlaceholder
                                                    handleOnChange={(mail) => this.handleChangeMail(mail, values)}
                                                />
                                            </Col>
                                        </Row>

                                        <Row>
                                            <Col xs={12} md={6} className="required">
                                                <Field
                                                    component={PhoneInput}
                                                    idForm={FORM_ID}
                                                    name="phone"
                                                    country="AR"
                                                    mobilePhone="54 11"
                                                    preferredCountries={["ar"]}
                                                />
                                            </Col>
                                        </Row>

                                        <Row>
                                            <Col xs={12} md={6}>
                                                <Field
                                                    component={TextField}
                                                    idForm={FORM_ID}
                                                    name="accountSummary"
                                                    type="text"
                                                    hidePlaceholder
                                                    value={i18n.get(`${FORM_ID}.accountSummary.monthly`)}
                                                    disabled
                                                />
                                            </Col>
                                        </Row>

                                        <I18n component="h2" id={`${FORM_ID}.contactsModalities.title`} />

                                        <I18n component="p" id={`${FORM_ID}.contactsModalities.info`} />

                                        <Row className="form-actions no-print">
                                            <Col xs={12} md={6}>
                                                <Button
                                                    className="btn-outline"
                                                    image="images/figma/icon/download.svg"
                                                    label={`${FORM_ID}.contactsModalities.spreadsheet`}
                                                    onClick={() =>
                                                        this.handleClickDownload("Modalidades_de_contacto_PLANILLA")
                                                    }
                                                />
                                            </Col>
                                        </Row>

                                        <Row>
                                            <Col xs={12} md={6} className="required">
                                                <FieldLabel
                                                    labelText={i18n.get(
                                                        `${FORM_ID}.contactsModalities.loadFile.title`,
                                                        "",
                                                    )}
                                                    idField="contactsModalitiesLabel"
                                                />
                                            </Col>

                                            <Field
                                                component={DocumentUploader}
                                                idForm={FORM_ID}
                                                name="contactsModalities"
                                                label={`${FORM_ID}.contactsModalities.loadFile.label`}
                                                allowedTypes="xlsx"
                                                documentCode={configUtils.get(
                                                    "investmentFunds.thuban.addAccount.contactsModalities.documentCode",
                                                    9002,
                                                )}
                                                defaultDocument={{
                                                    documentId: accountData?.documentToUpload?.documentName,
                                                    documentName: accountData?.documentToUpload?.documentName,
                                                }}
                                                withDocumentName
                                                accountData={accountData}
                                            />
                                        </Row>
                                        <I18n component="p" id={`${FORM_ID}.contactsModalities.help`} />
                                    </Container>
                                    <Container className="container--layout items-center" gridClassName="form-content">
                                        <Col xs={12} md={8} mdOffset={2} lg={6} lgOffset={3}>
                                            <Button
                                                bsStyle="primary"
                                                label="global.continue"
                                                loading={fetchingSubmit}
                                                type="submit"
                                                disabled={canSubmit}
                                            />
                                        </Col>
                                    </Container>
                                </Form>
                            );
                        }}
                    </Formik>
                </MainContainer>
            </PageLoading>
        );
    }
}
const mapStateToProps = (state) => ({
    fetchingSubmit: investmentfundsSelectors.getFetchingSubmit(state),
    fetching: investmentfundsSelectors.getFetching(state),
    expired: investmentfundsSelectors.getExpired(state),
    accountData: investmentfundsSelectors.getAccountData(state),
});
export default connect(mapStateToProps)(AddAccountForm);
